import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { format } from "date-fns";
import { ArrowLeft, CalendarIcon, Heart, ShieldCheck, Ban } from "lucide-react";
import { toast } from "sonner";

import { CHECKOUT } from "@/constants/testIds";
import { LOAD_TIERS, getTier } from "@/data/pricing";
import { TIME_SLOTS } from "@/data/timeSlots";
import { LOGO_URL } from "@/data/brand";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INPUT_CLS =
  "h-11 w-full rounded-none border-2 border-black bg-white px-3 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-[var(--kay-pink)]";

export default function CheckoutPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const initialLoad = params.get("load") || "small";

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    address_number: "",
    road: "",
    town: "",
    postcode: "",
    service_type: "domestic",
    load_size: LOAD_TIERS.find((t) => t.key === initialLoad)
      ? initialLoad
      : "small",
    preferred_time: TIME_SLOTS[1].value,
    notes: "",
  });
  const [date, setDate] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const tier = useMemo(() => getTier(form.load_size), [form.load_size]);

  const validate = () => {
    if (form.name.trim().length < 2) return "Please enter your full name.";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) return "Please enter a valid email.";
    if (form.phone.trim().length < 6) return "Please enter a contact number.";
    if (form.address_number.trim().length < 1) return "Please enter your house number or name.";
    if (form.road.trim().length < 2) return "Please enter your road.";
    if (form.town.trim().length < 2) return "Please enter your town or city.";
    if (form.postcode.trim().length < 3) return "Please enter your postcode.";
    if (!date) return "Please pick a preferred collection date.";
    if (!form.preferred_time) return "Please pick a preferred time slot.";
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        preferred_date: format(date, "yyyy-MM-dd"),
      };
      const { data } = await axios.post(`${API}/bookings`, payload);
      if (data?.booking_id) {
        navigate(`/checkout/success?booking_id=${encodeURIComponent(data.booking_id)}`);
      } else {
        toast.error("Couldn't submit booking. Please try again.");
        setSubmitting(false);
      }
    } catch (e2) {
      const detail =
        e2?.response?.data?.detail?.[0]?.msg ||
        e2?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Please check your details and try again.");
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid={CHECKOUT.root}
      className="min-h-screen bg-[var(--kay-bg)] text-black"
    >
      {/* Slim header */}
      <header className="border-b-2 border-black bg-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 md:px-8">
          <Link to="/" className="flex items-center gap-3">
            <img
              src={LOGO_URL}
              alt="Kay's Waste Management"
              className="h-11 w-11 rounded-full border-2 border-black bg-black object-cover"
            />
            <div className="leading-none">
              <div className="font-script text-xl text-[var(--kay-pink)]">Kay's</div>
              <div className="font-display text-[13px] uppercase tracking-widest text-[var(--kay-pink)]">
                Waste Management
              </div>
            </div>
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 border-2 border-black bg-white px-3 py-2 text-xs font-semibold uppercase tracking-widest hard-shadow-sm hover-lift"
          >
            <ArrowLeft size={14} /> Back
          </Link>
        </div>
      </header>

      <div className="hi-vis-stripe h-3 w-full" />

      <main className="mx-auto max-w-7xl px-4 py-12 md:px-8 md:py-16">
        <div className="mb-2 text-xs font-bold uppercase tracking-[0.25em] text-[var(--kay-pink)]">
          Book a collection
        </div>
        <h1 className="max-w-3xl font-display text-4xl uppercase leading-none sm:text-5xl md:text-6xl">
          Just a few details
          <br />
          and we're{" "}
          <span className="relative inline-block">
            <span className="relative z-10">on the way.</span>
            <span className="absolute inset-x-[-6px] bottom-1 -z-0 h-3 bg-[var(--kay-pink)]" />
          </span>
        </h1>

        <div className="mt-10 grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
          <form onSubmit={submit} className="lg:col-span-8">
            <div className="border-2 border-black bg-white p-6 hard-shadow md:p-8">
              {/* Contact */}
              <SectionHeader step="01" title="Your details" />
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <Label className="text-xs font-bold uppercase tracking-widest">Full name</Label>
                  <Input
                    data-testid={CHECKOUT.name}
                    value={form.name}
                    onChange={(e) => update("name", e.target.value)}
                    placeholder="Jane Smith"
                    className={INPUT_CLS}
                  />
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-widest">Email address</Label>
                  <Input
                    data-testid={CHECKOUT.email}
                    type="email"
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    placeholder="jane@example.co.uk"
                    className={INPUT_CLS}
                  />
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-widest">Contact number</Label>
                  <Input
                    data-testid={CHECKOUT.phone}
                    value={form.phone}
                    onChange={(e) => update("phone", e.target.value)}
                    placeholder="07700 900000"
                    className={INPUT_CLS}
                  />
                </div>
              </div>

              {/* Address */}
              <SectionHeader step="02" title="Pick-up address" className="mt-10" />
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-6">
                <div className="sm:col-span-2">
                  <Label className="text-xs font-bold uppercase tracking-widest">House no. / name</Label>
                  <Input
                    data-testid={CHECKOUT.addressNumber}
                    value={form.address_number}
                    onChange={(e) => update("address_number", e.target.value)}
                    placeholder="12 or 'Rose Cottage'"
                    className={INPUT_CLS}
                  />
                </div>
                <div className="sm:col-span-4">
                  <Label className="text-xs font-bold uppercase tracking-widest">Road</Label>
                  <Input
                    data-testid={CHECKOUT.road}
                    value={form.road}
                    onChange={(e) => update("road", e.target.value)}
                    placeholder="High Street"
                    className={INPUT_CLS}
                  />
                </div>
                <div className="sm:col-span-4">
                  <Label className="text-xs font-bold uppercase tracking-widest">Town / City</Label>
                  <Input
                    data-testid={CHECKOUT.town}
                    value={form.town}
                    onChange={(e) => update("town", e.target.value)}
                    placeholder="Weston-super-Mare"
                    className={INPUT_CLS}
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-xs font-bold uppercase tracking-widest">Postcode</Label>
                  <Input
                    data-testid={CHECKOUT.postcode}
                    value={form.postcode}
                    onChange={(e) => update("postcode", e.target.value.toUpperCase())}
                    placeholder="BS23 1AA"
                    className={INPUT_CLS}
                  />
                </div>
              </div>

              {/* Service + load */}
              <SectionHeader step="03" title="Service &amp; load" className="mt-10" />
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                  <Label className="text-xs font-bold uppercase tracking-widest">Service</Label>
                  <Select
                    value={form.service_type}
                    onValueChange={(v) => update("service_type", v)}
                  >
                    <SelectTrigger data-testid={CHECKOUT.serviceSelect} className={INPUT_CLS}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="rounded-none border-2 border-black">
                      <SelectItem value="domestic">Domestic Waste Clearance</SelectItem>
                      <SelectItem value="tradespeople">Tradespeople Waste</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-widest">Load size</Label>
                  <Select
                    value={form.load_size}
                    onValueChange={(v) => update("load_size", v)}
                  >
                    <SelectTrigger data-testid={CHECKOUT.loadSelect} className={INPUT_CLS}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="rounded-none border-2 border-black">
                      {LOAD_TIERS.map((t) => (
                        <SelectItem key={t.key} value={t.key}>
                          {t.name} — £{t.ourPrice}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Date & time */}
              <SectionHeader step="04" title="Preferred date &amp; time" className="mt-10" />
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                  <Label className="text-xs font-bold uppercase tracking-widest">Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        data-testid={CHECKOUT.date}
                        className={cn(
                          "flex h-11 w-full items-center justify-between border-2 border-black bg-white px-3 text-left text-sm",
                          !date && "text-neutral-500"
                        )}
                      >
                        {date ? format(date, "EEE, d MMM yyyy") : "Pick a date"}
                        <CalendarIcon size={16} />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent
                      align="start"
                      className="w-auto rounded-none border-2 border-black p-0"
                    >
                      <Calendar
                        mode="single"
                        selected={date}
                        onSelect={setDate}
                        disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-widest">Time slot</Label>
                  <Select
                    value={form.preferred_time}
                    onValueChange={(v) => update("preferred_time", v)}
                  >
                    <SelectTrigger data-testid={CHECKOUT.time} className={INPUT_CLS}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="rounded-none border-2 border-black">
                      {TIME_SLOTS.map((s) => (
                        <SelectItem key={s.value} value={s.value}>
                          {s.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="mt-5">
                <Label className="text-xs font-bold uppercase tracking-widest">
                  Notes (optional)
                </Label>
                <Textarea
                  data-testid={CHECKOUT.notes}
                  value={form.notes}
                  onChange={(e) => update("notes", e.target.value)}
                  placeholder="e.g. side access on the left, waste is in the back garden, old fridge…"
                  className="min-h-[110px] w-full rounded-none border-2 border-black bg-white px-3 py-2 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-[var(--kay-pink)]"
                />
              </div>

              {/* Insurance notice */}
              <div
                data-testid={CHECKOUT.insuranceNotice}
                className="mt-8 flex items-start gap-4 border-2 border-black bg-[var(--kay-pink)] p-5 text-black"
              >
                <div className="flex h-10 w-10 shrink-0 items-center justify-center border-2 border-black bg-white">
                  <Heart size={18} className="text-[var(--kay-pink)]" fill="currentColor" strokeWidth={0} />
                </div>
                <p className="text-sm leading-relaxed sm:text-base">
                  <span className="font-display uppercase tracking-widest">Please note</span>
                  {" — "}
                  our insurance does not allow us to transport waste through your home. All waste
                  should be outside of the property prior to collection (in the garden or side
                  access is fine). Thank you for your understanding{" "}
                  <Heart
                    size={14}
                    className="inline align-baseline text-black"
                    fill="currentColor"
                    strokeWidth={0}
                  />
                </p>
              </div>

              {/* Restricted items */}
              <div
                data-testid="checkout-restricted-notice"
                className="mt-6 border-2 border-black bg-white p-5 hard-shadow-sm sm:p-6"
              >
                <div className="flex items-start gap-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center border-2 border-black bg-black text-[var(--kay-pink)]">
                    <Ban size={18} />
                  </div>
                  <div>
                    <div className="font-display text-sm uppercase tracking-widest">
                      Items we can't take
                    </div>
                    <p className="mt-1 text-sm text-neutral-700">
                      We can't legally transport the items below — but no
                      stress: we'll happily bag them up for you when we attend
                      so you can dispose of them yourself.
                    </p>
                  </div>
                </div>
                <ul className="mt-5 grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
                  {[
                    "Asbestos",
                    "Car batteries",
                    "Raw meat (unless in sealed packaging)",
                    "Gas bottles",
                    "Oils or petrol",
                    "Fire extinguishers",
                    "Fluorescent lamps / light tubes",
                    "Paint or paint cans (unless empty)",
                    "Liquid chemicals or solvents",
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-3 text-sm">
                      <span className="mt-2 inline-block h-2 w-2 shrink-0 bg-[var(--kay-pink)]" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </form>

          {/* Summary column */}
          <aside className="lg:col-span-4">
            <div
              data-testid={CHECKOUT.loadSummary}
              className="sticky top-6 border-2 border-black bg-white p-6 hard-shadow"
            >
              <div className="text-xs font-bold uppercase tracking-widest text-[var(--kay-pink)]">
                Your booking
              </div>
              <div className="mt-2 font-display text-2xl uppercase leading-tight sm:text-3xl">
                {tier?.name}
              </div>
              <div className="mt-1 text-sm text-neutral-600">
                {tier?.equivalent} · {tier?.weight}
              </div>

              <dl className="mt-6 space-y-3 border-t-2 border-black pt-6 text-sm">
                <div className="flex items-center justify-between">
                  <dt className="text-neutral-600">Total price</dt>
                  <dd className="font-display text-lg">£{tier?.ourPrice}</dd>
                </div>
                {tier?.skipPrice && (
                  <div className="flex items-center justify-between text-neutral-500">
                    <dt>Skip hire (for reference)</dt>
                    <dd className="line-through">£{tier.skipPrice}</dd>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <dt className="text-neutral-600">Paid on collection</dt>
                  <dd className="font-display text-lg">£{tier?.ourPrice}</dd>
                </div>
              </dl>

              <button
                type="button"
                data-testid={CHECKOUT.payBtn}
                onClick={submit}
                disabled={submitting}
                className="mt-6 inline-flex w-full items-center justify-center gap-2 border-2 border-black bg-black px-6 py-4 font-display text-sm uppercase tracking-widest text-white hard-shadow-sm hover-lift disabled:opacity-60"
              >
                {submitting ? "Sending…" : "Request Collection"}
              </button>

              <div className="mt-4 flex items-center gap-2 text-xs text-neutral-500">
                <ShieldCheck size={14} className="text-[var(--kay-pink)]" />
                Upper Tier Licensed Waste Carrier — waste disposed of responsibly.
              </div>

              <p className="mt-3 text-xs text-neutral-500">
                No payment needed today. We'll confirm your booking and take payment on
                collection.
              </p>
            </div>
          </aside>
        </div>
      </main>
    </div>
  );
}

function SectionHeader({ step, title, className = "" }) {
  return (
    <div className={`mb-5 flex items-center gap-3 ${className}`}>
      <span className="inline-flex h-8 w-8 items-center justify-center border-2 border-black bg-[var(--kay-pink)] font-display text-xs text-black">
        {step}
      </span>
      <h2
        className="font-display text-lg uppercase tracking-tight sm:text-xl"
        dangerouslySetInnerHTML={{ __html: title }}
      />
    </div>
  );
}
